import React, { useRef, forwardRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, EffectFade, Keyboard, Navigation, Autoplay, Pagination, Mousewheel, FreeMode } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import Icon from '~/components/modules/icon';
import trackEvent from '~/hooks/useEventTracker';

const Carousel = ({
	name,
	children,
	className,
	carouselClassName,
	nextButton,
	prevButton,
	prevButtonClassName,
	nextButtonClassName,
	hideNav = false,
	...carouselProps
}) => {
	const prevRef = useRef();
	const nextRef = useRef();

	const arrowButtonClasses = `absolute top-1/2 z-10 -translate-y-1/2 opacity-60 text-white hover:opacity-95 focus:opacity-95 disabled:opacity-30 disabled:cursor-not-allowed transition ${hideNav ? 'hidden' : ''}`;

	const settings = {
		autoplay: false,
		autoHeight: true,
		freeMode: {
			enabled: true,
			sticky: true
		},
		keyboard: {
			'enabled': true
		},
		loop: true,
		mousewheel: {
			enabled: true,
			forceToAxis: true
		},
		spaceBetween: 30,
		navigation: {
			nextEl: '.next-btn',
			prevEl: '.prev-btn'
		},
		pagination: false,
		slidesPerView: 1,
		slideToClickedSlide: true,
		touchStartForcePreventDefault: true,
		touchStartPreventDefault: false,
		breakpoints: {
			640: {
				slidesPerView: 2
			},
			768: {
				slidesPerView: 3
			}
		},
		onBeforeInit: swiper => {
			swiper.params.navigation.prevEl = prevRef.current;
			swiper.params.navigation.nextEl = nextRef.current;
		},
		onSlideChange: swiper => {
			trackEvent('Engagement', 'Carousel', `${name ? `${name}: ` : null}Slide ${swiper.realIndex + 1}`);
		}
	};

	return (
		<div className={className ?? ''}>
			<Swiper
				modules={[A11y, EffectFade, Keyboard, Navigation, Autoplay, Pagination, Mousewheel, FreeMode]}
				{...settings}
				{...carouselProps}
				className={carouselClassName ?? ''}
			>
				{children?.map((slide, i) => (
					<SwiperSlide key={`${name}-carousel-${i}`}>
						{slide}
					</SwiperSlide>
				))}

				{prevButton ?
					prevButton
					:
					<PrevButton
						ref={prevRef}
						arrowButtonClasses={arrowButtonClasses}
						className={prevButtonClassName ?? ''}
					/>
				}
				{nextButton ?
					nextButton
					:
					<NextButton
						ref={nextRef}
						arrowButtonClasses={arrowButtonClasses}
						className={nextButtonClassName ?? ''}
					/>
				}
			</Swiper>
		</div>
	);
};

export const NextButton = forwardRef((
	{
		className,
		arrowButtonClasses
	},
	ref
) => {
	return (
		<button
			ref={ref}
			className={`
				next-btn right-2
				${arrowButtonClasses}
				${className ?? ''}
			`}
		>
			<Icon
				iconImage="mdi:arrow-right-drop-circle"
				sizeClasses="h-6 w-6"
			/>
			<span className="sr-only">Next slide</span>
		</button>
	);
});

export const PrevButton = forwardRef((
	{
		className,
		arrowButtonClasses
	},
	ref
) => {
	return (
		<button
			ref={ref}
			className={`
				prev-btn left-2
				${arrowButtonClasses}
				${className ?? ''}
			`}
		>
			<Icon
				iconImage="mdi:arrow-left-drop-circle"
				sizeClasses="h-6 w-6"
			/>
			<span className="sr-only">Previous slide</span>
		</button>
	);
});

export const CarouselItem = ({
	children
}) => {
	return (
		<>
			{children}
		</>
	);
};

NextButton.displayName = 'NextButton';
PrevButton.displayName = 'PrevButton';

Carousel.Item = CarouselItem;
export default Carousel;
